import { utcToZonedTime } from 'date-fns-tz';
import { TimePreferenceDay, TimePreference } from 'sb-lib/types';
import { cloneDeep } from 'lodash';

export const ONE_DAY = 60 * 60 * 24;
export const ONE_WEEK = ONE_DAY * 7;

/**
 * Gets the currently active TimePreference, if there is any, and enhances it
 * with timezone-aware dates for when the time starts and ends.
 */
export function getActiveTimePreference(
  tps: TimePreference[],
  timezone: string,
): TimePreference | undefined {
  const now = utcToZonedTime(new Date().getTime(), timezone);
  const day = now.getDay();
  const hour = now.getHours();
  const sortedTps = [...tps].sort(
    (tp1, tp2) =>
      TIME_PREFERENCE_PRIORITY_ORDER.indexOf(tp1.day) -
      TIME_PREFERENCE_PRIORITY_ORDER.indexOf(tp2.day),
  );
  const todaysTp = sortedTps.find(tp => {
    const dayArray = TIME_PREFERENCE_DAY_ARRAYS[tp.day];
    if (!dayArray) return false;
    return dayArray.includes(day);
  });
  if (todaysTp && hour >= todaysTp.start && hour < todaysTp.end) {
    return todaysTp;
  }
}

export function is12HourClock() {
  try {
    return Intl.DateTimeFormat(navigator.language, {
      hour: 'numeric',
    }).resolvedOptions().hour12;
  } catch {
    return true;
  }
}

export function getFirstDayOfWeek(): string {
  // Get more egalitarian some day
  return 'Sunday';
}

export function getDayName(day: number): string {
  const date: Date = new Date(2023, 4, 7 + day);

  const options: Intl.DateTimeFormatOptions = { weekday: 'short' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

export const TIME_PREFERENCE_LABEL: Record<TimePreferenceDay, string> = {
  [TimePreferenceDay.EVERYDAY]: 'Every day',
  [TimePreferenceDay.WEEKDAY]: 'Weekdays',
  [TimePreferenceDay.WEEKEND]: 'Weekends',
  [TimePreferenceDay.MONDAY]: 'Monday',
  [TimePreferenceDay.TUESDAY]: 'Tuesday',
  [TimePreferenceDay.WEDNESDAY]: 'Wednesday',
  [TimePreferenceDay.THURSDAY]: 'Thursday',
  [TimePreferenceDay.FRIDAY]: 'Friday',
  [TimePreferenceDay.SATURDAY]: 'Saturday',
  [TimePreferenceDay.SUNDAY]: 'Sunday',
};

// Specific days -> weekday/end -> everyday
export const TIME_PREFERENCE_PRIORITY_ORDER = [
  TimePreferenceDay.SUNDAY,
  TimePreferenceDay.MONDAY,
  TimePreferenceDay.TUESDAY,
  TimePreferenceDay.WEDNESDAY,
  TimePreferenceDay.THURSDAY,
  TimePreferenceDay.FRIDAY,
  TimePreferenceDay.SATURDAY,
  TimePreferenceDay.WEEKDAY,
  TimePreferenceDay.WEEKEND,
  TimePreferenceDay.EVERYDAY,
];

export const TIME_PREFERENCE_DAY_ARRAYS = {
  [TimePreferenceDay.EVERYDAY]: [0, 1, 2, 3, 4, 5, 6],
  [TimePreferenceDay.WEEKDAY]: [1, 2, 3, 4, 5],
  [TimePreferenceDay.WEEKEND]: [0, 6],
  [TimePreferenceDay.SUNDAY]: [0],
  [TimePreferenceDay.MONDAY]: [1],
  [TimePreferenceDay.TUESDAY]: [2],
  [TimePreferenceDay.WEDNESDAY]: [3],
  [TimePreferenceDay.THURSDAY]: [4],
  [TimePreferenceDay.FRIDAY]: [5],
  [TimePreferenceDay.SATURDAY]: [6],
};

export const HOUR_OPTIONS = [
  { value: 0, label: '12:00AM' },
  { value: 1, label: '1:00AM' },
  { value: 2, label: '2:00AM' },
  { value: 3, label: '3:00AM' },
  { value: 4, label: '4:00AM' },
  { value: 5, label: '5:00AM' },
  { value: 6, label: '6:00AM' },
  { value: 7, label: '7:00AM' },
  { value: 8, label: '8:00AM' },
  { value: 9, label: '9:00AM' },
  { value: 10, label: '10:00AM' },
  { value: 11, label: '11:00AM' },
  { value: 12, label: '12:00PM' },
  { value: 13, label: '1:00PM' },
  { value: 14, label: '2:00PM' },
  { value: 15, label: '3:00PM' },
  { value: 16, label: '4:00PM' },
  { value: 17, label: '5:00PM' },
  { value: 18, label: '6:00PM' },
  { value: 19, label: '7:00PM' },
  { value: 20, label: '8:00PM' },
  { value: 21, label: '9:00PM' },
  { value: 22, label: '10:00PM' },
  { value: 23, label: '11:00PM' },
  { value: 24, label: '12:00AM' },
];

// FIXME: For timezones utilizing daylight savings time the offset is not constant
export const TIMEZONE_OPTIONS = [
  { label: '(GMT-12:00) International Date Line West', value: 'Etc/GMT+12' },
  { label: '(GMT-11:00) Midway Island, Samoa', value: 'Pacific/Midway' },
  { label: '(GMT-10:00) Hawaii', value: 'Pacific/Honolulu' },
  { label: '(GMT-09:00) Alaska', value: 'US/Alaska' },
  { label: '(GMT-08:00) Pacific Time (US & Canada)', value: 'America/Los_Angeles' },
  { label: '(GMT-08:00) Tijuana, Baja California', value: 'America/Tijuana' },
  { label: '(GMT-07:00) Arizona', value: 'US/Arizona' },
  { label: '(GMT-07:00) Chihuahua, La Paz, Mazatlan', value: 'America/Chihuahua' },
  { label: '(GMT-07:00) Mountain Time (US & Canada)', value: 'US/Mountain' },
  { label: '(GMT-06:00) Central America', value: 'America/Managua' },
  { label: '(GMT-06:00) Central Time (US & Canada)', value: 'US/Central' },
  {
    label: '(GMT-06:00) Guadalajara, Mexico City, Monterrey',
    value: 'America/Mexico_City',
  },
  { label: '(GMT-06:00) Saskatchewan', value: 'Canada/Saskatchewan' },
  { label: '(GMT-05:00) Bogota, Lima, Quito, Rio Branco', value: 'America/Bogota' },
  { label: '(GMT-05:00) Eastern Time (US & Canada)', value: 'US/Eastern' },
  { label: '(GMT-05:00) Indiana (East)', value: 'US/East-Indiana' },
  { label: '(GMT-04:00) Atlantic Time (Canada)', value: 'Canada/Atlantic' },
  { label: '(GMT-04:00) Caracas, La Paz', value: 'America/Caracas' },
  { label: '(GMT-04:00) Manaus', value: 'America/Manaus' },
  { label: '(GMT-04:00) Santiago', value: 'America/Santiago' },
  { label: '(GMT-03:30) Newfoundland', value: 'Canada/Newfoundland' },
  { label: '(GMT-03:00) Brasilia', value: 'America/Sao_Paulo' },
  {
    label: '(GMT-03:00) Buenos Aires, Georgetown',
    value: 'America/Argentina/Buenos_Aires',
  },
  { label: '(GMT-03:00) Greenland', value: 'America/Godthab' },
  { label: '(GMT-03:00) Montevideo', value: 'America/Montevideo' },
  { label: '(GMT-02:00) Mid-Atlantic', value: 'America/Noronha' },
  { label: '(GMT-01:00) Cape Verde Is.', value: 'Atlantic/Cape_Verde' },
  { label: '(GMT-01:00) Azores', value: 'Atlantic/Azores' },
  { label: '(GMT+00:00) Casablanca, Monrovia, Reykjavik', value: 'Africa/Casablanca' },
  {
    label: '(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London',
    value: 'Etc/Greenwich',
  },
  {
    label: '(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
    value: 'Europe/Amsterdam',
  },
  {
    label: '(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague',
    value: 'Europe/Belgrade',
  },
  { label: '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris', value: 'Europe/Brussels' },
  { label: '(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb', value: 'Europe/Sarajevo' },
  { label: '(GMT+01:00) West Central Africa', value: 'Africa/Lagos' },
  { label: '(GMT+02:00) Amman', value: 'Asia/Amman' },
  { label: '(GMT+02:00) Athens, Bucharest, Istanbul', value: 'Europe/Athens' },
  { label: '(GMT+02:00) Beirut', value: 'Asia/Beirut' },
  { label: '(GMT+02:00) Cairo', value: 'Africa/Cairo' },
  { label: '(GMT+02:00) Harare, Pretoria', value: 'Africa/Harare' },
  {
    label: '(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
    value: 'Europe/Helsinki',
  },
  { label: '(GMT+02:00) Jerusalem', value: 'Asia/Jerusalem' },
  { label: '(GMT+02:00) Minsk', value: 'Europe/Minsk' },
  { label: '(GMT+02:00) Windhoek', value: 'Africa/Windhoek' },
  { label: '(GMT+03:00) Kuwait, Riyadh, Baghdad', value: 'Asia/Kuwait' },
  { label: '(GMT+03:00) Moscow, St. Petersburg, Volgograd', value: 'Europe/Moscow' },
  { label: '(GMT+03:00) Nairobi', value: 'Africa/Nairobi' },
  { label: '(GMT+03:00) Tbilisi', value: 'Asia/Tbilisi' },
  { label: '(GMT+03:30) Tehran', value: 'Asia/Tehran' },
  { label: '(GMT+04:00) Abu Dhabi, Muscat', value: 'Asia/Muscat' },
  { label: '(GMT+04:00) Baku', value: 'Asia/Baku' },
  { label: '(GMT+04:00) Yerevan', value: 'Asia/Yerevan' },
  { label: '(GMT+04:30) Kabul', value: 'Asia/Kabul' },
  { label: '(GMT+05:00) Yekaterinburg', value: 'Asia/Yekaterinburg' },
  { label: '(GMT+05:00) Islamabad, Karachi, Tashkent', value: 'Asia/Karachi' },
  { label: '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi', value: 'Asia/Calcutta' },
  { label: '(GMT+05:45) Kathmandu', value: 'Asia/Katmandu' },
  { label: '(GMT+06:00) Almaty, Novosibirsk', value: 'Asia/Almaty' },
  { label: '(GMT+06:00) Astana, Dhaka', value: 'Asia/Dhaka' },
  { label: '(GMT+06:30) Yangon (Rangoon)', value: 'Asia/Rangoon' },
  { label: '(GMT+07:00) Bangkok, Hanoi, Jakarta', value: 'Asia/Bangkok' },
  { label: '(GMT+07:00) Krasnoyarsk', value: 'Asia/Krasnoyarsk' },
  { label: '(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi', value: 'Asia/Hong_Kong' },
  { label: '(GMT+08:00) Kuala Lumpur, Singapore', value: 'Asia/Kuala_Lumpur' },
  { label: '(GMT+08:00) Irkutsk, Ulaan Bataar', value: 'Asia/Irkutsk' },
  { label: '(GMT+08:00) Perth', value: 'Australia/Perth' },
  { label: '(GMT+08:00) Taipei', value: 'Asia/Taipei' },
  { label: '(GMT+09:00) Osaka, Sapporo, Tokyo', value: 'Asia/Tokyo' },
  { label: '(GMT+09:00) Seoul', value: 'Asia/Seoul' },
  { label: '(GMT+09:00) Yakutsk', value: 'Asia/Yakutsk' },
  { label: '(GMT+09:30) Adelaide', value: 'Australia/Adelaide' },
  { label: '(GMT+09:30) Darwin', value: 'Australia/Darwin' },
  { label: '(GMT+10:00) Brisbane', value: 'Australia/Brisbane' },
  { label: '(GMT+10:00) Canberra, Melbourne, Sydney', value: 'Australia/Canberra' },
  { label: '(GMT+10:00) Hobart', value: 'Australia/Hobart' },
  { label: '(GMT+10:00) Guam, Port Moresby', value: 'Pacific/Guam' },
  { label: '(GMT+10:00) Vladivostok', value: 'Asia/Vladivostok' },
  { label: '(GMT+11:00) Magadan, Solomon Is., New Caledonia', value: 'Asia/Magadan' },
  { label: '(GMT+12:00) Auckland, Wellington', value: 'Pacific/Auckland' },
  { label: '(GMT+12:00) Fiji, Kamchatka, Marshall Is.', value: 'Pacific/Fiji' },
  { label: "(GMT+13:00) Nuku'alofa", value: 'Pacific/Tongatapu' },
];

function makeLocalTzOption() {
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Date().getTimezoneOffset();
  const offsetSign = offset < 0 ? '+' : '-';
  const offsetHours = Math.abs(Math.floor(offset / 60))
    .toString()
    .padStart(2, '0');
  const offsetMin = (offset % 60).toString().padStart(2, '0').toString();
  return {
    label: `(GMT${offsetSign}${offsetHours}:${offsetMin}) ${localTz}`,
    value: localTz,
  };
}

export function getTimezoneOptions() {
  const localOption = makeLocalTzOption();
  const options = cloneDeep(TIMEZONE_OPTIONS).filter(o => o.value !== localOption.value);
  options.push(localOption);
  options.sort((a, b) => (a.label > b.label ? 1 : -1));
  return options;
}
